import {useEffect, useMemo, useRef, useState} from 'react';
import {Select, Spin} from 'antd';
import {LoadingOutlined, SearchOutlined} from '@ant-design/icons';
import {useLazyQuery} from '@apollo/client';
import {GET_HELPDESK_FAQS} from 'graphql/queries/flowBuilder';
import {unwrap} from './utils';
import styles from './FlowBuilder.module.scss';

const DEBOUNCE_MS = 400;
const PAGE_SIZE = 25;
// Trigger the next-page fetch a little before the very bottom for a smoother feel.
const SCROLL_THRESHOLD_PX = 48;

const truncate = (s, max = 80) =>
  !s ? '' : s.length > max ? `${s.slice(0, max)}…` : s;

/**
 * antd Select (multiple) backed by `getHelpdeskFAQs`. Lets the admin search
 * by question text and pick FAQs — value remains an array of post-id strings
 * so the existing issue form wiring keeps working.
 *
 * Loading state covers both the debounce window and the in-flight request.
 */
export const FaqPicker = ({
  value = [],
  onChange,
  language = 'EN',
  initialFaqs,
}) => {
  const [search, setSearch] = useState('');
  const [knownById, setKnownById] = useState({});
  const [pendingDebounce, setPendingDebounce] = useState(false);
  // Accumulated FAQ rows for the current search term, across loaded pages.
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);
  const debounceRef = useRef(null);
  // The term `items` currently belongs to — guards against stale page appends
  // when the admin retypes mid-flight.
  const termRef = useRef('');
  // Blocks overlapping fetches (e.g. a scroll firing while a page is in flight).
  const fetchingRef = useRef(false);

  const [runSearch, {loading: requestLoading}] = useLazyQuery(
    GET_HELPDESK_FAQS,
    {fetchPolicy: 'no-cache', errorPolicy: 'all'},
  );

  // Combined: "user is typing" OR "request in flight".
  const loading = pendingDebounce || requestLoading;
  const hasMore = page > 0 && page < pages;

  // Cancel any pending timer on unmount.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Seed labels from the issue's already-attached FAQs.
  useEffect(() => {
    if (!Array.isArray(initialFaqs) || !initialFaqs.length) return;
    setKnownById(prev => {
      const next = {...prev};
      initialFaqs.forEach(f => {
        if (f?.postid != null) next[String(f.postid)] = f;
      });
      return next;
    });
  }, [initialFaqs]);

  const mergeKnown = rows => {
    if (!rows.length) return;
    setKnownById(prev => {
      const next = {...prev};
      rows.forEach(f => {
        if (f?.postid != null) next[String(f.postid)] = f;
      });
      return next;
    });
  };

  // Fetch a single page for `term`. pageToLoad === 1 starts a fresh result set;
  // higher pages append. Stale responses (term changed mid-flight) are dropped.
  const fetchPage = async (term, pageToLoad) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await runSearch({
        variables: {
          input: {
            language,
            ...(term ? {search: term} : {}),
            pagination: {page: pageToLoad, limit: PAGE_SIZE, paginate: true},
          },
        },
      });
      // A newer search superseded this request — ignore its rows.
      if (termRef.current !== term) return;
      const node = res?.data?.getHelpdeskFAQs;
      const {rows} = unwrap(node);
      setItems(prev => (pageToLoad === 1 ? rows : [...prev, ...rows]));
      setPage(node?.pagination?.page ?? pageToLoad);
      setPages(node?.pagination?.pages ?? pageToLoad);
      setHasFetched(true);
      mergeKnown(rows);
    } finally {
      fetchingRef.current = false;
    }
  };

  const scheduleSearch = (term, delay = DEBOUNCE_MS) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPendingDebounce(true);
    termRef.current = term;
    debounceRef.current = setTimeout(() => {
      setPendingDebounce(false);
      fetchPage(term, 1);
    }, delay);
  };

  const handleSearch = term => {
    setSearch(term);
    const trimmed = (term || '').trim();
    // Reset the paged result set for the new term.
    setItems([]);
    setPage(0);
    setPages(0);
    if (!trimmed) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setPendingDebounce(false);
      termRef.current = '';
      // Reload the unfiltered first page so clearing the box isn't a dead end.
      scheduleSearch('');
      return;
    }
    scheduleSearch(trimmed);
  };

  // Fire one (debounced) initial search on first focus so the dropdown isn't empty.
  const handleFocus = () => {
    if (!hasFetched && !requestLoading && !pendingDebounce) {
      scheduleSearch('', 0);
    }
  };

  // Load the next page once the user scrolls near the bottom of the list.
  const handlePopupScroll = e => {
    const {scrollTop, scrollHeight, clientHeight} = e.target;
    const nearBottom =
      scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD_PX;
    if (nearBottom && hasMore && !loading && !fetchingRef.current) {
      fetchPage(termRef.current, page + 1);
    }
  };

  const stringValue = (value || []).map(v => String(v));

  const options = useMemo(() => {
    // The dropdown shows the CURRENT fetch only (search results or the loaded
    // pages) — NOT the full accumulated `knownById`, which would never shrink
    // and make every search look unfiltered.
    const merged = new Map();
    items.forEach(f => merged.set(String(f.postid), f));

    // Keep already-selected values present so their chips render with a proper
    // label even when they're not in the current result set. `knownById` is
    // only used for this — never to populate the visible list wholesale.
    stringValue.forEach(id => {
      if (!merged.has(id) && knownById[id]) merged.set(id, knownById[id]);
    });

    return Array.from(merged.values()).map(f => ({
      value: String(f.postid),
      label: `#${f.postid} · ${truncate(f.question)}`,
      title: f.question,
    }));
  }, [items, knownById, stringValue]);

  return (
    <Select
      mode="multiple"
      value={stringValue}
      onChange={next => onChange(next)}
      onSearch={handleSearch}
      onFocus={handleFocus}
      onPopupScroll={handlePopupScroll}
      onBlur={() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setPendingDebounce(false);
        setSearch('');
      }}
      searchValue={search}
      filterOption={false}
      placeholder="Search FAQs by question…"
      loading={loading}
      suffixIcon={
        loading ? (
          <LoadingOutlined spin style={{color: '#6366f1'}} />
        ) : (
          <SearchOutlined style={{color: '#94a3b8'}} />
        )
      }
      options={options}
      optionLabelProp="label"
      dropdownRender={menu => (
        <>
          {menu}
          {requestLoading && page > 0 && options.length > 0 ? (
            <div
              style={{
                padding: 8,
                textAlign: 'center',
                color: '#94a3b8',
                borderTop: '1px solid #f1f5f9',
              }}>
              <Spin size="small" />{' '}
              <span style={{marginLeft: 8}}>Loading more…</span>
            </div>
          ) : null}
        </>
      )}
      notFoundContent={
        loading ? (
          <div style={{padding: 12, textAlign: 'center', color: '#475569'}}>
            <Spin size="small" />{' '}
            <span style={{marginLeft: 8}}>
              {pendingDebounce ? 'Waiting…' : 'Searching FAQs…'}
            </span>
          </div>
        ) : search ? (
          <div style={{padding: 12, textAlign: 'center', color: '#94a3b8'}}>
            No FAQs match <strong style={{color: '#475569'}}>“{search}”</strong>
            .
          </div>
        ) : (
          <div style={{padding: 12, textAlign: 'center', color: '#94a3b8'}}>
            No FAQs available.
          </div>
        )
      }
      className={styles.faqPicker}
    />
  );
};
